// API helper for the embedded Shopify app SPA.
//
// AppBridge v4.x exposes the `shopify` global via `useAppBridge()`. Session
// tokens are minted by calling `shopify.idToken()` (callable). This replaces
// the v3 `getSessionToken(app)` import from plan-doc — that helper was
// removed in v4. (R8 deviation.)

import type { useAppBridge } from '@shopify/app-bridge-react';
import { InstallError, type InstallErrorCode, type InstallResponse } from './types';

// The shape returned by `useAppBridge()` — `@shopify/app-bridge-types` isn't
// in our direct dependencies, so we derive the type through the hook.
export type ShopifyAppBridge = ReturnType<typeof useAppBridge>;

const KNOWN_INSTALL_ERROR_CODES: ReadonlyArray<InstallErrorCode> = [
  'no_connection_for_shop',
  'token_exchange_failed',
];

export function makeApi(app: ShopifyAppBridge, workerOrigin: string) {
  async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const token = await app.idToken();
    return fetch(`${workerOrigin}${path}`, {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  }

  return {
    async getDashboard() {
      const res = await authedFetch('/api/shopify/dashboard');
      if (!res.ok) throw new Error(`dashboard ${res.status}`);
      return res.json();
    },
    /**
     * Token Exchange bootstrap. Called once on SPA mount before the dashboard
     * renders. Idempotent — repeat calls short-circuit to `{status:'cached'}`
     * on the backend.
     */
    async postInstall(): Promise<InstallResponse> {
      const res = await authedFetch('/api/shopify/install', { method: 'POST' });
      let body: { status?: 'cached' | 'exchanged'; error?: string } = {};
      try {
        body = (await res.json()) as typeof body;
      } catch {
        // Non-JSON body — treat as 'unknown' downstream.
      }
      if (!res.ok) {
        const code: InstallErrorCode =
          typeof body.error === 'string' && (KNOWN_INSTALL_ERROR_CODES as readonly string[]).includes(body.error)
            ? (body.error as InstallErrorCode)
            : 'unknown';
        throw new InstallError(code, res.status);
      }
      if (body.status !== 'cached' && body.status !== 'exchanged') {
        throw new InstallError('unknown', res.status, 'malformed install response');
      }
      return { status: body.status };
    },
    /**
     * @deprecated The PortalButton now opens a direct portal deep link
     * (`${VITE_PORTAL_URL}/connectors/${connectionId}`) and no longer hits
     * `POST /api/shopify/portal-link`. The worker route still exists but
     * will be rewritten in #406; do not add new callers to this helper.
     */
    async getPortalLink(): Promise<{ url: string }> {
      const res = await authedFetch('/api/shopify/portal-link', { method: 'POST' });
      const body = (await res.json()) as { url?: string; error?: string };
      if (!res.ok) throw new Error(body.error ?? `portal-link ${res.status}`);
      if (!body.url) throw new Error('portal-link response missing url');
      return { url: body.url };
    },
  };
}
